/* -----------------Globals--------------- */
import { useCallback, useEffect, useState } from "react";

/* -----------------Helpers & Hooks--------------- */
import { trackCustomEvent } from "analytics";
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import { getShouldShowReviewPrompt } from "../helpers";
import { useReviewCache } from "./useReviewCache";

/* -----------------Constants--------------- */
import { feedbackEvents } from "../constants";

import { events } from "@constants/tracking";
import { getStoreUrl } from "@helpers/url";
import useAnalytics from "@hooks/useAnalytics";
import { useGlobalStore } from "@hooks/useGlobalStore";
/* -----------------Third party libraries--------------- */
import { Linking } from "react-native";

export const useReview = () => {
	const [showReviewPrompt, setShouldShowReviewPrompt] = useState(false);

	const { isRoutedFromSession } = useLocalSearchParams<{
		isRoutedFromSession: string;
	}>();
	const reviewCache = useReviewCache({
		isRoutedFromSession: Boolean(isRoutedFromSession),
		shouldInvalidate: true,
	});
	const {
		markReviewLeft,
		markCouldBeBetter,
		markSaidMaybeLater,
		isLoading: isLoadingReviewState,
	} = reviewCache;

	const { explicitCallTrackCustomEvent } = useAnalytics(undefined, false);

	const isPremiumUser = useGlobalStore((state) => state.isPremiumUser);

	useFocusEffect(
		useCallback(() => {
			if (isPremiumUser === undefined || isLoadingReviewState) {
				return;
			}
			const shouldShowReviewPrompt = getShouldShowReviewPrompt(
				isPremiumUser,
				reviewCache,
			);
			setShouldShowReviewPrompt(shouldShowReviewPrompt);
		}, [isPremiumUser, reviewCache, isLoadingReviewState]),
	);

	const showReviewDialog = showReviewPrompt && isRoutedFromSession;

	useEffect(() => {
		if (showReviewDialog) {
			const eventName = feedbackEvents.reviewDialogOpened;
			explicitCallTrackCustomEvent({
				eventName,
			});
		}
	}, [showReviewDialog, explicitCallTrackCustomEvent]);

	const handleDismiss = () => {
		explicitCallTrackCustomEvent({
			eventName: feedbackEvents.reviewDialogClosed,
		});
		setShouldShowReviewPrompt(false);
	};

	const handleFallbackReview = () => {
		const url = getStoreUrl();
		if (url) {
			Linking.openURL(url);
		}
	};

	const handleReviewButtonsClick = (type: "positive" | "negative") => {
		if (type === "positive") {
			trackCustomEvent({
				eventName: feedbackEvents.reviewPositiveClicked,
			});
			markReviewLeft();
			handleFallbackReview();
		} else {
			markCouldBeBetter();
			trackCustomEvent({
				eventName: feedbackEvents.reviewNegativeClicked,
			});
			router.push("/feedback?view=home");
		}
		handleDismiss();
	};

	const handleMaybeLater = () => {
		trackCustomEvent({
			eventName: events.reviewPromptMayBeLaterClicked,
		});
		markSaidMaybeLater();
		handleDismiss();
	};

	return {
		showReviewPrompt,
		isRoutedFromSession,
		handleReviewButtonsClick,
		handleDismiss,
		handleMaybeLater,
		isLoadingReviewState,
		showReviewDialog,
	};
};
